import React from 'react';
import { Button, Card } from '../ui';

type ProfileImageCardProps = {
  profileFileInputRef: React.RefObject<HTMLInputElement>;
  handleProfileFileSelect: (_e: React.ChangeEvent<HTMLInputElement>) => void;
  profileImageUrl: string;
  profilePrompt: string;
  setProfileModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  profileGenerating: boolean;
  profileUploading: boolean;
  profileDeleting: boolean;
  profileSettingIg: boolean;
  handleProfileDelete: () => void;
  hasSession: boolean;
  handleSetIgProfilePicture: () => void;
};

export function ProfileImageCard({
  profileFileInputRef,
  handleProfileFileSelect,
  profileImageUrl,
  profilePrompt,
  setProfileModalOpen,
  profileGenerating,
  profileUploading,
  profileDeleting,
  profileSettingIg,
  handleProfileDelete,
  hasSession,
  handleSetIgProfilePicture,
}: ProfileImageCardProps) {
  return (
    <Card elevation="raised">
      <Card.Header>
        <Card.Title subtitle="Your Instagram account avatar. Generate one on-brand (your brand icon is folded in automatically) or upload your own, then push it live to Instagram.">
          <span className="settings-section-icon">🪪</span> Profile Picture
        </Card.Title>
      </Card.Header>
      <Card.Body className="settings-card-body">
        <input
          ref={profileFileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={handleProfileFileSelect}
        />
        <div className="settings-brand-icon-row">
          <div
            className={`settings-profile-preview${profileImageUrl ? '' : ' settings-profile-preview--empty'}`}
            role={profileImageUrl ? undefined : 'button'}
            tabIndex={profileImageUrl ? -1 : 0}
            onClick={() => {
              if (!profileImageUrl) profileFileInputRef.current?.click();
            }}
            onKeyDown={(e) => {
              if (!profileImageUrl && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                profileFileInputRef.current?.click();
              }
            }}
            aria-label={profileImageUrl ? 'Profile picture preview' : 'Upload profile picture'}
          >
            {profileImageUrl ? (
              <img src={profileImageUrl} alt="Profile" />
            ) : (
              <>
                <span style={{ fontSize: 28 }}>🪪</span>
                <span>Click to upload</span>
              </>
            )}
          </div>
          <div className="settings-brand-icon-info">
            <p className="settings-brand-icon-info__title">
              {profileImageUrl ? 'Your profile picture is ready' : 'Create your account avatar'}
            </p>
            <p className="settings-brand-icon-info__desc">
              Instagram crops profile pictures to a circle — the preview shows the live crop. When
              you generate, we fold in your brand icon and account style so it stays on-brand.
            </p>
            {profilePrompt && (
              <p className="settings-brand-icon-info__compress" title={profilePrompt}>
                <strong>Last prompt:</strong> {profilePrompt}
              </p>
            )}
            <div className="settings-brand-icon-actions">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setProfileModalOpen(true)}
                loading={profileGenerating}
                disabled={profileUploading || profileDeleting || profileSettingIg}
              >
                {profileImageUrl ? '✨ Regenerate with AI' : '✨ Generate with AI'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => profileFileInputRef.current?.click()}
                loading={profileUploading}
                disabled={profileGenerating || profileDeleting || profileSettingIg}
              >
                📁 {profileImageUrl ? 'Replace upload' : 'Upload image'}
              </Button>
              {profileImageUrl && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleProfileDelete}
                  loading={profileDeleting}
                  disabled={profileGenerating || profileUploading || profileSettingIg}
                >
                  Remove
                </Button>
              )}
            </div>
            {profileImageUrl && (
              <div className="settings-profile-publish">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSetIgProfilePicture}
                  loading={profileSettingIg}
                  disabled={!hasSession || profileGenerating || profileUploading || profileDeleting}
                >
                  📲 Set as Instagram profile picture
                </Button>
                <p className="settings-brand-icon-info__desc" style={{ marginTop: 6 }}>
                  {hasSession
                    ? 'Pushes this image straight to your connected Instagram account.'
                    : 'Connect your Instagram account above to push it live.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
